// src/app/store.js
import { configureStore, createListenerMiddleware } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

import authReducer, {
  grantsRenewed,
  logout,
  recordedSession,
  sessionRestored,
} from "features/auth/authSlice";
import callApplicationReducer from "features/callApplicationSlice/callApplicationSlice";
import languageReducer from "features/language/languageSlice";

import { activeCaseApi } from "components/services/active-case/ActiveCaseService";
import { applicationApi } from "components/services/application/ApplicationService"; // Import the applicationApi
import { fileApi } from "components/services/application-file/ApplicationFile";
import { authApi } from "components/services/authorization/authService";
import { bankApi } from "components/services/bank/bankService";
import { callApplicationApi } from "components/services/call-application/CallApplicationService";
import { callApplicationFileApi } from "components/services/call-application-file/CallApplicationFile";
import { commentApi } from "components/services/comment/commentService";
import { contactInformationApi } from "components/services/contact-information/ContactInformationService";
import { feedbackApi } from "components/services/feedback/feedbackService";
import { messagesApi } from "components/services/messages/messagesService";
import { reportApi } from "components/services/report/reportService";
import { reportScheduleApi } from "components/services/report-schedule/reportScheduleService";
import { templateCommentApi } from "components/services/template-comment/templateCommentService";
import { templateFileApi } from "components/services/template-file/templateFileService";
import { userApi } from "components/services/users/userService";
import {
  isSignedOutInAnotherTab,
  onGrantsRenewed,
  onSessionRefused,
} from "components/utilities/sedra3/auth";

// Every RTK Query API of the application. The store registers each one from this
// list, and a logout empties all of their caches.
export const apiSlices = [
  authApi,
  userApi,
  fileApi,
  callApplicationFileApi,
  callApplicationApi,
  applicationApi,
  commentApi,
  contactInformationApi,
  templateCommentApi,
  feedbackApi,
  messagesApi,
  reportScheduleApi,
  bankApi,
  templateFileApi,
  reportApi,
  activeCaseApi,
];

// The UI texts are the same for everyone, and the toasts read them from this
// cache on the sign-in page too; they are not data of the user.
const publicApiSlices = [messagesApi];

// Data loaded for a signed-in user stays in these caches until they are reset;
// after a logout nothing of it may be shown again (VAPT Portal 5).
export const clearApiCaches = () => (dispatch) => {
  apiSlices
    .filter((api) => !publicApiSlices.includes(api))
    .forEach((api) => dispatch(api.util.resetApiState()));
};

const session = createListenerMiddleware();
session.startListening({
  actionCreator: logout,
  effect: (_, { dispatch }) => dispatch(clearApiCaches()),
});

export const store = configureStore({
  reducer: {
    auth: authReducer,
    language: languageReducer,
    callApplication: callApplicationReducer,
    ...Object.fromEntries(
      apiSlices.map((api) => [api.reducerPath, api.reducer])
    ),
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .prepend(session.middleware)
      .concat(apiSlices.map((api) => api.middleware)),
});

// An earlier version of the portal kept the tokens in localStorage and the
// access token in a cookie its script wrote; neither is left within reach of
// the page's scripts (#112). A cookie the server set HttpOnly under the same
// name is not the script's to change, so only the old copy goes.
localStorage.removeItem("accessToken");
localStorage.removeItem("refreshToken");
document.cookie = "access_token=; path=/; max-age=0";

// A signed-in browser keeps the session's record, not its tokens: the page
// starts signed in and renews the access token, which the server refuses if
// the session is over (index.jsx).
const recorded = recordedSession();
if (recorded) {
  store.dispatch(sessionRestored(recorded));
}

// When the server refuses to renew the session, this tab signs out: the
// renewal decides it (auth.js), for every request that asked for it, and only
// on a refusal — not when the portal could not be reached (#121). The other
// tabs follow through the storage event below.
onSessionRefused(() => store.dispatch(logout()));

// Every renewal names the account's roles and permissions as the server holds
// them now, and a page load renews the session (index.jsx): the page shows
// what the account may do after every reload (VAPT Portal 2).
onGrantsRenewed((grants) => store.dispatch(grantsRenewed(grants)));

// When another tab of this browser signs out, this one signs out the same way:
// the logout clears its state and caches, and the signed-in pages send it to
// the sign-in page (#118). Only a tab still signed in acts, so the events of
// one sign-out (one per token) log it out once.
window.addEventListener("storage", (event) => {
  if (store.getState().auth.isAuthenticated && isSignedOutInAnotherTab(event)) {
    store.dispatch(logout());
  }
});

// Optional: setup listeners for refetching queries on focus/reconnect
setupListeners(store.dispatch);
